import React, { createContext, useCallback, useContext } from 'react'
import { useBoolean, useEffectOnce, useSetState } from 'react-use'

import { ErrorResponse } from '@makyoapp/makyo-sdk/api/api'
import { Users2FAAPI } from '@makyoapp/makyo-sdk/api/Users'

import { useReqError } from '@/hooks/useReqError'

interface TwoFaValue {
  qr?: File
  secret: string
  enabled: boolean
  showSetup: boolean
  needSetup: boolean
  scratch: string[]
  showBackup: boolean
}

const initialState: TwoFaValue = {
  qr: undefined,
  secret: '',
  enabled: false,
  showSetup: false,
  needSetup: false,
  scratch: [],
  showBackup: true,
}

interface CbOptions {
  onSuccess?: () => void
  onFailed?: (e: ErrorResponse) => void
  onFinally?: () => void
}

interface TwoFaState {
  value: TwoFaValue
  setValue: (nextValue: Partial<TwoFaValue>) => void
  showResetSuccess: boolean
  setShowResetSuccess: (show: boolean) => void
  isLoading: boolean
  setIsLoading: (loading: boolean) => void
  handleGetQr: (successCb: () => void) => void
  handleEnable: (code: string, options?: CbOptions) => void
  handleDisable: (code: string, options: CbOptions) => void
  handleReset: (code: string, options?: CbOptions) => void
}
const TwoFaContext = createContext<TwoFaState>({
  value: initialState,
  setValue: () => null,
  isLoading: false,
  setIsLoading: () => null,
  showResetSuccess: false,
  setShowResetSuccess: () => null,
  handleGetQr: () => null,
  handleEnable: () => null,
  handleDisable: () => null,
  handleReset: () => null,
})

export function useTwoFaContext() {
  return useContext(TwoFaContext)
}

interface TwoFacProviderProps {
  children?: React.ReactNode
}

/**
 * this context provider handle:
 * - the 2fa setting
 * - enable / disable user 2fa setting
 * - reset 2fa setting
 *
 */
export const TwoFaProvider = ({ children }: TwoFacProviderProps) => {
  const [value, setValue] = useSetState(initialState)
  const [isLoading, setIsLoading] = useBoolean(false)
  const [showResetSuccess, setShowResetSuccess] = useBoolean(false)
  const printAPIError = useReqError()

  // handle updating status from backend
  const handleChange = useCallback(
    (initial: boolean, scratch?: string[], onComplete?: () => void) => {
      setIsLoading(true)
      Users2FAAPI.ReqGetStatus()
        .then((status) => {
          setValue({
            enabled: status._Enabled,
            needSetup: !status._Setup,
            scratch: scratch ? scratch : [],
          })
          if (initial) {
            setValue({ showBackup: !status._Setup })
          }
        })
        .catch((e) => {
          printAPIError(e)
        })
        .finally(() => {
          onComplete?.()
          setIsLoading(false)
        })
    },
    [setIsLoading, setValue, printAPIError]
  )

  // Handle get qr code from API
  const handleGetQr = useCallback(
    (onSuccess?: () => void) => {
      setIsLoading(true)
      Users2FAAPI.ReqGetQr()
        .then((res) => {
          setValue({
            qr: res._Qr,
            secret: res._Secret,
          })
          onSuccess?.()
        })
        .catch((e) => {
          printAPIError(e)
        })
        .finally(() => setIsLoading(false))
    },
    [setIsLoading, setValue, printAPIError]
  )

  // handle disabling 2fa
  const handleDisable = useCallback(
    (code: string, options?: CbOptions) => {
      Users2FAAPI.ReqDisable({
        _Password: code,
      })
        .then(() => {
          handleChange(false)
          options?.onSuccess?.()
        })
        .catch((e) => {
          printAPIError(e)
          options?.onFailed?.(e)
        })
        .finally(() => options?.onFinally?.())
    },
    [handleChange, printAPIError]
  )

  // handle reset key
  const handleReset = useCallback(
    (code: string, options?: CbOptions) => {
      Users2FAAPI.ReqReset({
        _Password: code,
      })
        .then(() => {
          setValue({
            showSetup: false,
            scratch: [],
            secret: '',
            qr: undefined,
            showBackup: true,
          })
          handleChange(false, [], () => {
            setShowResetSuccess(true)
          })
          options?.onSuccess?.()
        })
        .catch((e) => {
          printAPIError(e)
          options?.onFailed?.(e)
        })
        .finally(() => options?.onFinally?.())
    },
    [handleChange, setShowResetSuccess, setValue, printAPIError]
  )

  // handle enable 2fa
  const handleEnable = useCallback(
    (code: string, options?: CbOptions) => {
      Users2FAAPI.ReqEnable({
        _Password: code,
      })
        .then((res) => {
          handleChange(false, res._ScratchCodes)
          options?.onSuccess?.()
        })
        .catch((e) => {
          setIsLoading(false)
          printAPIError(e)
          options?.onFailed?.(e)
        })
        .finally(() => {
          options?.onFinally?.()
        })
    },
    [setIsLoading, handleChange, printAPIError]
  )

  useEffectOnce(() => {
    handleChange(true)
  })

  return (
    <TwoFaContext.Provider
      value={{
        value,
        setValue,
        isLoading,
        setIsLoading,
        showResetSuccess,
        setShowResetSuccess,
        handleGetQr,
        handleDisable,
        handleReset,
        handleEnable,
      }}
    >
      {children}
    </TwoFaContext.Provider>
  )
}
